<app-page-header [title]="'navigation.contributions' | transloco" />

<main class="p-4">
  <!-- Filters -->
  <div class="mb-4 flex flex-wrap items-center justify-between gap-4">
    @if (availableStatuses.length > 1) {
      <div class="flex flex-wrap items-end gap-4">
        <!-- Status Filter -->
        <div class="min-w-[150px]">
          <span class="mb-1 block text-sm font-medium">{{ 'contributions.filters.status' | transloco }}</span>
          <p-select
            [options]="statusOptions"
            [(ngModel)]="statusFilter"
            (onChange)="onFilterChange()"
            [placeholder]="'contributions.statusOptions.allPlaceholder' | transloco"
            [style]="{ width: '100%' }"
          />
        </div>

        <!-- Reset -->
        <div>
          <p-button
            icon="pi pi-filter-slash"
            (onClick)="resetFilters()"
            [text]="true"
            [rounded]="true"
            [pTooltip]="'contributions.filters.resetButton' | transloco"
          />
        </div>
      </div>
    } @else {
      <div></div>
    }

    <!-- New Contribution Button -->
    <p-button
      [label]="'contributions.newContribution' | transloco"
      icon="pi pi-plus"
      (onClick)="openNewContributionModal()"
    />
  </div>

  <!-- Table -->
  <p-table
    [value]="contributions"
    [paginator]="false"
    styleClass="p-datatable-sm"
  >
    <ng-template #header>
      <tr>
        <th>{{ 'contributions.table.trip' | transloco }}</th>
        <th>{{ 'contributions.table.date' | transloco }}</th>
        <th>{{ 'contributions.table.user' | transloco }}</th>
        <th>{{ 'contributions.table.projects' | transloco }}</th>
        <th>{{ 'contributions.table.amount' | transloco }}</th>
        <th>{{ 'contributions.table.status' | transloco }}</th>
      </tr>
    </ng-template>

    <ng-template #body let-contribution>
      <tr
        class="cursor-pointer hover:bg-gray-50 h-14"
        (click)="viewDetails(contribution.id)"
      >
        <!-- Trip (first column) -->
        <td>
          <div class="max-w-[250px]">
            <span
              class="font-medium"
              [pTooltip]="contribution.trip.title"
            >
              {{ contribution.trip.title | slice: 0:40 }}{{ contribution.trip.title.length > 40 ? '...' : '' }}
            </span>
            <div class="mt-1 text-xs text-gray-500">
              <span>{{ contribution.trip.travellers }} {{ 'contributions.table.travelers' | transloco }}</span>
            </div>
          </div>
        </td>

        <!-- Date -->
        <td>{{ formatDate(contribution.createdAt) }}</td>

        <!-- User -->
        <td>
          @if (contribution.user) {
            <div>
              <div class="font-medium">
                {{ contribution.user.firstName }} {{ contribution.user.lastName }}
              </div>
              <div class="text-xs text-gray-500">
                {{ contribution.user.email }}
              </div>
            </div>
          } @else {
            <span class="text-gray-400">-</span>
          }
        </td>

        <!-- Projects -->
        <td>
          <div class="flex flex-wrap gap-1">
            @for (pc of contribution.projectContributions; track pc.id) {
              <span
                class="inline-block rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700"
                [pTooltip]="pc.projectName"
                tooltipPosition="top"
              >
                {{ pc.projectName }}
              </span>
            }
          </div>
        </td>

        <!-- Amount (TTC) -->
        <td class="font-medium">{{ formatAmount(contribution.totalAmount) }}</td>

        <!-- Status -->
        <td>
          <span
            class="inline-block rounded-full px-3 py-1 text-xs font-medium"
            [ngClass]="getStatusClasses(contribution.status)"
          >
            {{ 'contribute.status.' + contribution.status | transloco }}
          </span>
        </td>
      </tr>
    </ng-template>

    <ng-template #emptymessage>
      <tr>
        <td colspan="6" class="py-12">
          <div class="flex flex-col items-center justify-center">
            <div class="flex items-center gap-3">
              <i class="pi pi-inbox text-4xl text-gray-400"></i>
              <p class="text-gray-500">{{ 'contributions.empty.title' | transloco }}</p>
            </div>
            @if (statusFilter) {
              <p class="mt-2 text-sm text-gray-400">
                {{ 'contributions.empty.message' | transloco }}
                <span
                  class="cursor-pointer text-primary-500 hover:underline"
                  (click)="resetFilters()"
                  (keydown.enter)="resetFilters()"
                  (keydown.space)="resetFilters(); $event.preventDefault()"
                  tabindex="0"
                  role="button"
                  >{{ 'contributions.empty.resetLink' | transloco }}</span
                >
              </p>
            }
          </div>
        </td>
      </tr>
    </ng-template>
  </p-table>

  <!-- Custom Pagination -->
  <div class="mt-4 flex items-center justify-between">
    <div class="flex items-center gap-2">
      <span class="text-sm text-gray-600">{{ 'contributions.pagination.itemsPerPage' | transloco }}</span>
      <p-select
        [options]="pageSizeOptions"
        [(ngModel)]="pageSize"
        (onChange)="onPageSizeChange()"
        [style]="{ width: '80px' }"
      />
      <span class="ml-4 text-sm text-gray-600">
        Total: {{ totalRecords }} {{ 'contributions.pagination.contributionCount' | transloco }}
      </span>
    </div>

    <div class="flex items-center gap-2">
      <p-button
        icon="pi pi-angle-left"
        (onClick)="onPageChange(currentPage - 1)"
        [disabled]="currentPage === 1"
        [text]="true"
      />

      <span class="text-sm text-gray-600">
        {{ 'contributions.pagination.pageOf' | transloco: { current: currentPage, total: totalPages || 1 } }}
      </span>

      <p-button
        icon="pi pi-angle-right"
        (onClick)="onPageChange(currentPage + 1)"
        [disabled]="currentPage === totalPages || totalPages === 0"
        [text]="true"
      />
    </div>
  </div>
</main>

<p-toast />

<!-- Select Trip Modal -->
<app-select-trip-modal />
